import { useState, useEffect } from 'react'
import ButterflyBG from '@/assets/images/backgrounds/ButterflyBG.jpg'
import { Loader2, Image as ImageIcon, Calendar, MapPin, Building2 } from 'lucide-react'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import ScrollIndicator from '@/components/ScrollIndicator'
import VancouverMap from '@/components/VancouverMap'
import { projectService, ProjectLocation } from '@/services/ProjectService'

export default function OurWork() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [projects, setProjects] = useState<ProjectLocation[]>([])

  useEffect(() => {
    fetchProjects()
  }, [])

  const fetchProjects = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch projects for the map
      const projectData = await projectService.getProjectsForMap()
      setProjects(projectData)
    } catch (err) {
      console.error('Error fetching projects:', err)
      setError('Failed to load projects. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  const categories = [
    { id: 'all', name: 'All Projects' },
    { id: 'commercial', name: 'Commercial' },
    { id: 'residential', name: 'Residential' },
    { id: 'industrial', name: 'Industrial' },
    { id: 'infrastructure', name: 'Infrastructure' }
  ]

  if (loading) {
    return (
      <>
        <Header currentPage="work" />
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading our projects...</p>
          </div>
        </div>
        <Footer />
      </>
    )
  }

  if (error) {
    return (
      <>
        <Header currentPage="work" />
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="text-red-600 mb-4">
              <ImageIcon className="w-12 h-12 mx-auto mb-2" />
              <p className="text-lg font-semibold">Oops!</p>
            </div>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={fetchProjects}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </>
    )
  }

  return (
    <>
      <Header currentPage="work" />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image - Fixed */}
        <div className="fixed inset-0 w-full h-full z-0">
          <img
            src={ButterflyBG}
            alt="TAKT Construction Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight">
              Our Work
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-12 px-4">
              Explore our portfolio of exceptional projects that showcase our expertise in construction and innovation.
            </p>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <ScrollIndicator targetId="map" />
      </section>

      {/* Interactive Map Section */}
      <section id="map" className="py-16 sm:py-24 bg-gray-50 relative z-10">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 sm:mb-6">
              Our Projects
            </h2>
          </div>

          {/* Interactive Map */}
          <div className="max-w-6xl mx-auto">
            <VancouverMap className="h-[600px] w-full" projects={projects} />
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-16 sm:py-24 bg-white/80 backdrop-blur-sm relative z-10">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 sm:mb-6">
              Featured Projects
            </h2>
            <p className="text-lg sm:text-xl text-slate-600 max-w-3xl mx-auto px-4 mb-8">
              From commercial complexes to residential foundations, each project demonstrates our commitment to quality and innovation.
            </p>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-8">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    selectedCategory === category.id
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-gray-100/90 backdrop-blur-sm text-gray-700 hover:bg-gray-200/90'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* Coming Soon Message */}
          <div className="text-center py-16">
            <div className="max-w-md mx-auto">
              <div className="bg-gradient-to-br from-blue-50/90 to-indigo-50/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg">
                <ImageIcon className="w-16 h-16 text-blue-400 mx-auto mb-6" />
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Coming Soon</h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Our project portfolio is being prepared with stunning visuals and detailed case studies. 
                  Check back soon to explore our latest work!
                </p>
                
                {/* Preview Features */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
                  <div className="text-center">
                    <Building2 className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                    <p className="text-sm font-medium text-slate-700">Project Showcases</p>
                  </div>
                  <div className="text-center">
                    <Calendar className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                    <p className="text-sm font-medium text-slate-700">Timeline Details</p>
                  </div>
                  <div className="text-center">
                    <MapPin className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                    <p className="text-sm font-medium text-slate-700">Location Info</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  )
} 